import { BuilderComponent, useIsPreviewing } from '@builder.io/react'
import { nextTick } from '@patrianna/shared-utils'
import cx from 'classnames'
import { notFound, useParams, usePathname, useRouter } from 'next/navigation'

import { isBuilderContentEmpty } from 'app/utils/isBuilderContentEmpty'
import GameInfoMedia from 'components/GameInfoMedia'
import Page from 'components/Page'
import { ImageType } from 'components/Picture/Picture.types'
import FavoriteButton from 'containers/FavoriteButton'
import GamePagePlayButton from 'containers/GamePagePlayButton'
import { useRouterPushWithCallback } from 'containers/NavigationProvider'
import useCurrencyRedirect from 'hooks/useCurrencyRedirect'
import GameLanding from 'pageContainers/GameLandingPage'
import GameInfo from 'pageContainers/GameLandingPage/components/GameInfo'
import PlayGameButton from 'pageContainers/GameLandingPage/components/PlayGameButton'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { startExternalGame, startExternalGameFlow } from 'store/modules/slotGameFlow/actions'
import { isLoggedInSelector } from 'store/modules/user/selectors'
import { useThemeContext } from 'theme/themeProvider'
import { slugToTitle } from 'utils/slugToTitle'

import classes from './styles.module.scss'

import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { ProviderCode } from '@patrianna/shared-patrianna-types/store/ProviderModule'

export type Props = {
  pageProps: {
    page?: any
    gameProduct?: GameProduct
    defaultContent?: string
    meta?: any
  }
  pageModel: string
  providerCodes?: ProviderCode[]
}

function GameDescriptionPageViaBuilder(props: Props) {
  const { page, gameProduct, defaultContent } = props.pageProps
  const { pageModel, providerCodes } = props
  const { isDarkMode } = useThemeContext()
  const routerPush = useRouterPushWithCallback()
  const params = useParams()
  const router = useRouter()
  const isPreviewing = useIsPreviewing()
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const dispatch = useAppDispatch()
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)
  const pathname = usePathname()
  const testIdType = slugToTitle(pathname.split('/').filter(Boolean)[0])

  useCurrencyRedirect({ shouldCheckDirectLink: false })

  function handleLinkClick(e: any) {
    e.preventDefault()
    if (e.target.closest('a')) {
      const url = e.target.closest('a').attributes.href.value
      routerPush(url)
    }
  }

  if (!gameProduct?.code) {
    return notFound()
  }

  // PLDV-8952: per product decision, descriptions of geo-blocked games stay
  // visible (the one exception to the block). Lobby tiles are hidden and the
  // play page shows GameBlockedByLocation, so Play from here ends on that screen.

  const hasNoBuilderContent = !page || isBuilderContentEmpty(page)

  if (hasNoBuilderContent && !isPreviewing && gameProduct?.code) {
    return (
      <GameLanding
        activeCurrency={activeCurrency}
        gameProduct={gameProduct}
        pageContent={defaultContent}
        providerCodes={providerCodes}
      />
    )
  }

  // builder component
  return (
    <Page>
      <div hidden data-testid='game-title'>
        {gameProduct.title}
      </div>
      <div hidden data-testid='game-type'>
        {testIdType}
      </div>
      {gameProduct?.code && gameProduct.type !== 'bingo' && (
        <>
          <div className={classes.info}>
            <GameInfoMedia
              imagePath={'backgrounds'}
              imageType={ImageType.BACKGROUND}
              className={classes.imageContainer}
              gameProduct={gameProduct}
              dataTestId='game-banner'
              isGameDescription
            />
            <GameInfo
              isLoggedIn={isLoggedIn}
              gameProduct={gameProduct}
              activeCurrency={activeCurrency}
              providerCodes={providerCodes}
            />
          </div>
          <PlayGameButton
            className={classes.playGameButton}
            gameProduct={gameProduct}
            activeCurrency={activeCurrency}
            startExternalGameFlow={(game, currency) =>
              nextTick().then(() =>
                dispatch(startExternalGameFlow(game, currency, routerPush, { refererPath: 'gameDescription' })),
              )
            }
          />
          <div onClick={handleLinkClick} className={classes.gameDescription} data-testid='game-description'>
            <BuilderComponent model={pageModel} content={page} />
          </div>
          <GameInfoMedia gameProduct={gameProduct} imagePath={'screenshots'} imageType={ImageType.SCREENSHOT_CONTENT} />
        </>
      )}

      {gameProduct?.code && gameProduct.type === 'bingo' && (
        <div className={classes.bingoDescription} onClick={handleLinkClick}>
          <BuilderComponent model={pageModel} content={page} />
          <FavoriteButton
            className={cx(classes.descriptionFavorite, isDarkMode && classes.dark)}
            gameProduct={gameProduct}
            refererPath='game_description'
          />
          <GamePagePlayButton
            className={classes.playGameButton}
            caption='Enter this Room'
            openGame={() => {
              nextTick().then(() =>
                dispatch(startExternalGame({ type: 'bingo', code: params.roomId as string }, router.push)),
              )
            }}
          />
        </div>
      )}
    </Page>
  )
}

export default GameDescriptionPageViaBuilder
